/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { Box, Flex, IconButton, Text, VStack } from '@chakra-ui/react';
import gsap from 'gsap';
import AppRoutes from 'config/AppRoutes';
import PageLayout from 'ui/layout/PageLayout';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  AnswersSelector,
  BusinessChoiceSelector,
  PanelSelector,
} from 'store/selectors';
import { useEffect, useRef, useState, useCallback } from 'react';
import { updatePanel } from 'store/answers/answersSlice';
import VideoBlock from 'components/VideoBlock';
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from 'react-icons/md';
import Slider, { CustomArrowProps } from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { AssetType, getAsset } from 'utils/hooks/useAsset';
import useStepNavigation from 'utils/hooks/useStepNavigation';
import getFirstExistingTranslation from 'utils/getFirstExistingTranslation';
import usePanelList from './hooks/usePanelList';

const NextArrow = (props: CustomArrowProps) => {
  const { onClick } = props;
  return (
    <Box
      position="absolute"
      right="-20px"
      top={{ base: '40%', md: '40%' }}
      transform="translate(0, -50%)"
      zIndex={100}
    >
      <IconButton
        aria-label="select next panel"
        colorScheme="whiteAlpha"
        onClick={onClick}
        fontSize={{
          base: '250%',
          md: '350%',
        }}
        background="none"
        _active={{ background: 'none' }}
        _selected={{ background: 'none' }}
        _hover={{ background: 'none' }}
        icon={<MdKeyboardArrowRight color="black" />}
        zIndex={10}
      />
    </Box>
  );
};

const PrevArrow = (props: CustomArrowProps) => {
  const { onClick } = props;
  return (
    <Box
      position="absolute"
      left="-20px"
      top={{ base: '40%', md: '40%' }}
      transform="translate(0, -50%)"
      zIndex={100}
    >
      <IconButton
        aria-label="select previous panel"
        colorScheme="whiteAlpha"
        onClick={onClick}
        fontSize={{
          base: '250%',
          md: '350%',
        }}
        padding="0px"
        margin="0px"
        background="none"
        _active={{ background: 'none' }}
        _selected={{ background: 'none' }}
        _hover={{ background: 'none' }}
        icon={<MdKeyboardArrowLeft color="black" />}
        zIndex={10}
      />
    </Box>
  );
};

const PagePanel = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const currentRoute = AppRoutes.PANEL_SELECTION;
  let userPanel = useSelector(PanelSelector);
  const panelList = usePanelList();
  const { navigateToNextStep, navigateToPreviousStep } =
    useStepNavigation(currentRoute);
  const answers = useSelector(AnswersSelector);
  const businessChoice = useSelector(BusinessChoiceSelector);

  // security, be sure the user panel ID is in the list.
  if (userPanel && panelList && !panelList.some((p) => p.id === userPanel)) {
    console.warn(
      `No panel found in list: ${userPanel}, we automatically select first one`
    );
    userPanel = undefined;
  }

  const [currentPanel, setCurrentPanel] = useState(
    userPanel || panelList[0].id
  );
  const previewRef = useRef<HTMLDivElement>(null);

  const handleSelect = useCallback(
    (selection: string) => {
      dispatch(updatePanel(selection));
      setCurrentPanel(selection);
    },
    [dispatch]
  );

  const handleContinue = () => {
    navigateToNextStep();
  };

  useEffect(() => {
    if (previewRef.current) {
      previewRef.current.style.opacity = '0';
      gsap.killTweensOf(previewRef.current);
      gsap.to(previewRef.current, {
        autoAlpha: 1,
        duration: 0.8,
        delay: 0.3,
        scale: 1,
        startAt: {
          autoAlpha: 0,
          zIndex: -1,
          scale: 0.9,
        },
      });
    }

    if (!userPanel) {
      handleSelect(currentPanel);
    }
  }, [userPanel, handleSelect, currentPanel]);

  // --------------------- render ------------------------

  return (
    <PageLayout
      title={t('PANEL_SELECTION_title')}
      buttonLabel={t('common.continue')}
      buttonCallback={handleContinue}
      backButton
      backCallback={navigateToPreviousStep}
      buttonDisabled={!userPanel}
      buttonSticky={false}
    >
      <Box width={{ base: '90vw', md: '600px' }}>
        <Slider
          arrows
          afterChange={(idx) => {
            handleSelect(panelList[idx].id);
          }}
          prevArrow={<PrevArrow />}
          nextArrow={<NextArrow />}
        >
          {panelList.map((panel) => {
            const panelTitle = getFirstExistingTranslation(t, i18n, [
              `PANEL_SELECTION_panel_${panel.assetIndex}_${businessChoice}.title`,
              `PANEL_SELECTION_panel_${panel.assetIndex}.title`,
            ]);
            return (
              <Flex key={panel.id} id={panel.id} height="100%" flexDir="column">
                <VStack
                  width="full"
                  display="flex"
                  justifyContent="space-between"
                  alignSelf="start"
                >
                  <Text
                    fontSize="22px"
                    fontWeight="bold"
                    color="secondaryColor"
                    position="relative"
                    top="40px"
                  >
                    {panelTitle}
                  </Text>
                  <Box
                    height={{ base: '200px', md: '300px' }}
                    width="full"
                    ref={previewRef}
                  >
                    <VideoBlock
                      mt={{ base: '-100px', md: '-50px' }}
                      position="relative"
                      videoUrl={getAsset(
                        AssetType.VIDEO,
                        `/01-panneaux/PANNEAU0${panel.assetIndex} 1000.mp4`,
                        answers
                      )}
                      height="400px"
                      width="full"
                      fadInOnLoad
                    />
                  </Box>
                </VStack>
                <Text
                  width="full"
                  fontSize={{ base: 'sm', md: 'md' }}
                  textAlign="center"
                  // mt={{ base: '40px' }}
                  pb="5"
                >
                  {t(`PANEL_SELECTION_panel_${panel.assetIndex}.info`)}
                </Text>
              </Flex>
            );
          })}
        </Slider>
      </Box>
    </PageLayout>
  );
};

export default PagePanel;
